/**
 * Copyright (c) 2025 TechDivision GmbH
 * All rights reserved
 *
 * This product includes proprietary software developed at TechDivision GmbH, Germany
 * For more information see https://www.techdivision.com/
 *
 * To obtain a valid license for using this software please contact us at
 * license@techdivision.com
 *
 * @author Eugen Becker <e.becker@techdivision.com>
 */

import { useEffect, useState } from 'react';

/**
 *
 * @param {array<{name:string,unit:string,calorificValue:number}>} energyTypes
 */
export const useHeatCalculator = ({ energyTypes }) => {
  const [selectedEnergy, setSelectedEnergy] = useState(null);
  const [annualConsumption, setAnnualConsumption] = useState('');
  const [result, setResult] = useState(null);
  const [errors, setErrors] = useState({ energy: '', consumption: '' });

  const calculateHeatValues = (consumption, energy) => {
    const annualEfficiencyExistingHeating = 0.85;
    const fullLoadHours = 1900;

    const heatDemand = consumption * energy.calorificValue * annualEfficiencyExistingHeating;
    const connectionLoad = Math.round(heatDemand / fullLoadHours);
    return { heatDemand, connectionLoad };
  };

  const annualConsumptionOnChange = (event) => {
    const value = event.target.value;
    if (value.trim() === '') {
      setAnnualConsumption('');
    } else {
      setAnnualConsumption(Number(value));
    }
  };

  const findEnergy = (name) => {
    return energyTypes.find((energyType) => energyType.name === name) || null;
  };

  const isSelected = (name) => {
    return selectedEnergy?.name === name;
  };

  const selectEnergy = (name) => {
    if (selectedEnergy && selectedEnergy.name === name) {
      return;
    }
    const energy = findEnergy(name);
    if (!energy) return;
    setSelectedEnergy(energy);
    setResult(null);
    setAnnualConsumption('');
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    let newErrors = { energy: '', consumption: '' };
    let hasError = false;

    if (!selectedEnergy) {
      newErrors.energy = 'Bitte wählen Sie eine Energieart aus.';
      hasError = true;
    }
    if (annualConsumption === '' || annualConsumption < 0) {
      newErrors.consumption = 'Bitte geben Sie eine gültige Verbrauchsmenge ein.';
      hasError = true;
    }

    setErrors(newErrors);
    if (hasError) return;

    const calculatedResult = calculateHeatValues(annualConsumption, selectedEnergy);
    setResult(calculatedResult);
  };

  // set initial first energy type
  useEffect(() => {
    const firstEnergyType = energyTypes[0] ?? undefined;
    if (firstEnergyType) {
      setSelectedEnergy(firstEnergyType);
    }
  }, []);

  return {
    findEnergy,
    isSelected,
    handleSubmit,
    selectEnergy,
    annualConsumptionOnChange,
    annualConsumption,
    result,
    errors,
  };

};
